import { FormEvent, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { landingPagesApi } from '@/api/landingPages';
import QuestionRenderer from '@/components/landing/QuestionRenderer';
import type { LandingPageQuestion, SubmitSignupPayload } from '@/types';

const TURNSTILE_SITE_KEY = 'YOUR_TURNSTILE_SITE_KEY';

type AnswerValue = string | string[] | boolean | number;

type TurnstileWidget = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
    }
  ) => string;
  remove?: (widgetId: string) => void;
};

type TurnstileWindow = Window & {
  turnstile?: TurnstileWidget;
};

const inputClassName =
  'border border-gray-300 rounded-lg px-3 py-2 w-full text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';

function isEmptyAnswer(value: AnswerValue | undefined) {
  if (value == null) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'number') return Number.isNaN(value);
  return String(value).trim() === '';
}

function validateEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function StatusMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-white px-4 py-16 text-center text-gray-900">
      <div className="mx-auto max-w-lg rounded-lg border border-gray-200 p-8 shadow-sm">
        <p className="text-lg font-semibold">{children}</p>
      </div>
    </div>
  );
}

export default function PublicLandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [honeypot, setHoneypot] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileScriptLoaded, setTurnstileScriptLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const {
    data: page,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['landing-page-public', slug],
    queryFn: () => landingPagesApi.getBySlug(slug!),
    enabled: !!slug,
    retry: false,
  });

  const turnstileIsPlaceholder = TURNSTILE_SITE_KEY === 'YOUR_TURNSTILE_SITE_KEY';
  const submitDisabled = submitting || (!turnstileIsPlaceholder && turnstileToken === '');

  useEffect(() => {
    const existing = document.getElementById('cf-turnstile-script');
    const markLoaded = () => setTurnstileScriptLoaded(true);

    if (existing) {
      if ((window as TurnstileWindow).turnstile) {
        markLoaded();
      } else {
        existing.addEventListener('load', markLoaded);
        return () => existing.removeEventListener('load', markLoaded);
      }
      return;
    }

    const script = document.createElement('script');
    script.id = 'cf-turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.addEventListener('load', markLoaded);
    document.body.appendChild(script);

    return () => script.removeEventListener('load', markLoaded);
  }, []);

  useEffect(() => {
    if (!page || !turnstileRef.current || !turnstileScriptLoaded || widgetIdRef.current) return;

    const turnstile = (window as TurnstileWindow).turnstile;
    if (!turnstile) return;

    widgetIdRef.current = turnstile.render(turnstileRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: string) => setTurnstileToken(token),
      'expired-callback': () => setTurnstileToken(''),
    });

    return () => {
      if (widgetIdRef.current && turnstile.remove) {
        turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
      setTurnstileToken('');
    };
  }, [page, turnstileScriptLoaded]);

  const updateAnswer = (question: LandingPageQuestion, value: AnswerValue) => {
    setAnswers((current) => ({ ...current, [question.id]: value }));
    setErrors((current) => ({ ...current, [question.id]: '' }));
  };

  const validateForm = () => {
    const nextErrors: Record<string, string> = {};

    if (!firstName.trim()) {
      nextErrors.first_name = 'First name is required.';
    }

    if (!lastName.trim()) {
      nextErrors.last_name = 'Last name is required.';
    }

    if (!email.trim()) {
      nextErrors.email = 'Email is required.';
    } else if (!validateEmail(email)) {
      nextErrors.email = 'Enter a valid email address.';
    }

    if (!phone.trim()) {
      nextErrors.phone = 'נא להזין מספר טלפון';
    }

    for (const question of page?.questions ?? []) {
      if (question.required && isEmptyAnswer(answers[question.id])) {
        nextErrors[question.id] = 'This field is required.';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError('');

    if (honeypot !== '' && slug) {
      navigate(`/join/${slug}/thanks`);
      return;
    }

    if (!validateForm() || !page || !slug) return;

    const fullName = `${firstName} ${lastName}`.trim();

    const payload: SubmitSignupPayload = {
      landing_page_id: page.id,
      full_name: fullName,
      email: email.trim(),
      phone: phone.trim(),
      answers,
      turnstile_token: turnstileToken,
    };

    try {
      setSubmitting(true);
      await landingPagesApi.submitSignup(payload);
      navigate(`/join/${slug}/thanks`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-4 text-gray-600">
        Loading...
      </div>
    );
  }

  if (isError || !page) {
    return <StatusMessage>This page doesn't exist or is no longer available.</StatusMessage>;
  }

  if (page.status === 'paused') {
    return <StatusMessage>Signups are temporarily paused. Check back soon.</StatusMessage>;
  }

  if (page.status === 'closed') {
    return <StatusMessage>This signup is now closed.</StatusMessage>;
  }

  const accent = page.theme?.accent || '#6366f1';
  const bullets = page.theme?.bullets ?? [];

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{ '--accent': accent } as CSSProperties}
    >
      <div
        className="px-4 pb-20 pt-12 text-white"
        style={{
          background: `linear-gradient(135deg, ${accent} 0%, color-mix(in srgb, ${accent} 55%, #000) 100%)`,
        }}
      >
        <div className="mx-auto max-w-2xl text-center">
          {page.theme?.logo_url && (
            <img
              src={page.theme.logo_url}
              alt=""
              className="mx-auto mb-6 max-h-14 max-w-[160px] object-contain"
            />
          )}
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {page.hero_headline || page.title}
          </h1>
          {page.hero_subtext && (
            <p className="mt-4 text-base leading-7 text-white/80 sm:text-lg">
              {page.hero_subtext}
            </p>
          )}
          {bullets.length > 0 && (
            <ul className="mt-8 inline-flex flex-col gap-3 text-right" dir="rtl">
              {bullets.map((bullet, index) => (
                <li key={index} className="flex items-start gap-3 text-sm text-white/90 sm:text-base">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/20 text-white">
                    ✓
                  </span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mx-auto -mt-12 max-w-lg px-4 pb-16">
        <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200">
          <div dir="rtl">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="first-name" className="block text-sm font-medium text-gray-700 mb-1">
                    שם פרטי <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="first-name"
                    type="text"
                    value={firstName}
                    onChange={(event) => {
                      setFirstName(event.target.value);
                      setErrors((current) => ({ ...current, first_name: '' }));
                    }}
                    className={inputClassName}
                  />
                  {errors.first_name && <p className="text-xs text-red-500 mt-1">{errors.first_name}</p>}
                </div>

                <div>
                  <label htmlFor="last-name" className="block text-sm font-medium text-gray-700 mb-1">
                    שם משפחה <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="last-name"
                    type="text"
                    value={lastName}
                    onChange={(event) => {
                      setLastName(event.target.value);
                      setErrors((current) => ({ ...current, last_name: '' }));
                    }}
                    className={inputClassName}
                  />
                  {errors.last_name && <p className="text-xs text-red-500 mt-1">{errors.last_name}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  אימייל <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setErrors((current) => ({ ...current, email: '' }));
                  }}
                  className={inputClassName}
                />
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                  נייד <span className="text-red-500">*</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(event) => {
                    setPhone(event.target.value);
                    setErrors((current) => ({ ...current, phone: '' }));
                  }}
                  className={inputClassName}
                  placeholder="05X-XXXXXXX"
                />
                {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
              </div>

              {(page.questions ?? []).map((question) => (
                <QuestionRenderer
                  key={question.id}
                  question={question}
                  value={answers[question.id] ?? ''}
                  onChange={(value) => updateAnswer(question, value)}
                  error={errors[question.id]}
                />
              ))}

              <input
                type="text"
                name="website"
                value={honeypot}
                onChange={(event) => setHoneypot(event.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: 'absolute', opacity: 0, height: 0, pointerEvents: 'none' }}
              />

              <div ref={turnstileRef} className="mt-4" />

              {submitError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={submitDisabled}
                className="w-full rounded-lg px-4 py-3 text-sm font-semibold text-white shadow-sm transition-opacity disabled:cursor-not-allowed disabled:opacity-60"
                style={{ backgroundColor: 'var(--accent, #6366f1)' }}
              >
                {submitting ? 'שולח...' : page.cta_label}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
